import React, { Suspense, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import Request from 'components/Request';
import { __ } from 'i18n/index';
import PlayBar from 'react/home/PlayBar.jsx';
import StreamRail from 'react/home/StreamRail.jsx';
import { LiveMatchesPanel, RecentMatchesPanel, SearchersPanel, HowItWorks } from 'react/home/HubPanels.jsx';
import { applyLiveMatchesMessage } from 'react/home/liveMatches';
import { subscribeToLiveMatches } from 'session/liveMatchesSocket';
import {
	currentMatchFromDock,
	currentMatchFromDockOnServer,
	isHomeMatchColumnShowing,
	isHomeMatchColumnShowingOnServer,
	subscribeToMatchDockPlacement,
} from 'react/dock/matchDockPlacement';
import useMounted from 'react/hooks/useMounted';
import { assignPage } from 'navigation/pageLocation';

// Browser only: the server render never draws it (see the comment on BroadcastHub).
const HomeMatchColumn = React.lazy(() => import(/* webpackChunkName: "HomeMatchColumn" */ 'react/home/HomeMatchColumn'));

const ALL_GAMES = 'all';
const DEFAULT_LADDER_ID = 2; // Melee

function globalSiteUrl() {
	return typeof siteUrl === 'undefined' ? '' : siteUrl;
}

/**
 * The homepage: live Smash streams first, with a one-click path into a match
 * that skips the chat-room UI, and the matches being played right now, kept
 * current over the live_matches socket channel (session/liveMatchesSocket). The page ships the overview as initialOverview
 * (services\intro\HomePageProps); without it, the hub fetches
 * /api/v1/broadcast_hub/overview (Broadcast_Hub_Domain::overview).
 *
 * The ssr service renders it from those props. renderedAt (unix seconds, set by
 * PHP) times the recent matches, so the server and hydrating renders agree; the
 * Twitch embed needs the browser's hostname and appears once mounted.
 *
 * A member's match column (react/home/HomeMatchColumn: the step panel over the
 * match chat, at 1440px and wider) also appears only once mounted. The server
 * has neither the match (currentMatch here is only the opponent's name for the
 * play bar; the full match is in the site's store, which startup fills in the
 * browser) nor the window width, so the server and hydrating renders leave it
 * out and match. When it shows, the hub lays out as column | page.
 */
export default function BroadcastHub({
	isLoggedIn,
	preferredLadderIds,
	initialOverview = null,
	currentMatch = null,
	news = [],
	siteUrl = globalSiteUrl(),
	renderedAt = null,
	navigate = (url) => assignPage(url),
}) {
	const mounted = useMounted();
	const [overview, setOverview] = useState(initialOverview);
	const [loadFailed, setLoadFailed] = useState(false);
	const [selectedTab, setSelectedTab] = useState(ALL_GAMES);
	const [chosenChannel, setChosenChannel] = useState(initialOverview ? initialOverview.featured_channel : null);
	const [activeLadderIds, setActiveLadderIds] = useState(preferredLadderIds || []);
	const [playLadderId, setPlayLadderId] = useState(initialOverview ? defaultLadderId(initialOverview.games, preferredLadderIds) : null);
	const [liveMatches, setLiveMatches] = useState(initialOverview ? initialOverview.live_matches || [] : []);
	const matchColumnShowing = useSyncExternalStore(subscribeToMatchDockPlacement, isHomeMatchColumnShowing, isHomeMatchColumnShowingOnServer);
	// The server's match until the dock reports the one it shows, so the play
	// bar follows a match that ends or starts after the page loaded.
	const dockMatch = useSyncExternalStore(subscribeToMatchDockPlacement, currentMatchFromDock, currentMatchFromDockOnServer);
	const playMatch = dockMatch === undefined ? currentMatch : dockMatch;

	useEffect(() => {
		if(initialOverview) {
			return;
		}
		Request.apiv1Get('broadcast_hub/overview')
			.then((response) => {
				setOverview(response.overview);
				setChosenChannel(response.overview.featured_channel);
				setPlayLadderId(defaultLadderId(response.overview.games, preferredLadderIds));
				setLiveMatches(response.overview.live_matches || []);
			})
			.catch(() => setLoadFailed(true));
	}, []);

	useEffect(() => subscribeToLiveMatches((message) => {
		setLiveMatches((current) => applyLiveMatchesMessage(current, message));
	}), []);

	const gamesById = useMemo(() => {
		const byId = {};
		(overview ? overview.games : []).forEach((game) => {
			byId[game.ladder_id] = game;
		});
		return byId;
	}, [overview]);

	if(loadFailed) {
		return <div className='broadcast-hub broadcast-hub--failed'>{__('The homepage could not load. Refresh to try again.')}</div>;
	}
	if(!overview) {
		return <div className='broadcast-hub broadcast-hub--loading' aria-busy='true' />;
	}

	const visibleStreams = selectedTab === ALL_GAMES
		? overview.streams
		: overview.streams.filter((stream) => stream.ladder_id === selectedTab);
	const visibleLiveMatches = selectedTab === ALL_GAMES
		? liveMatches
		: liveMatches.filter((match) => match.ladder_id === selectedTab);
	const featuredStream = visibleStreams.find((stream) => stream.channel === chosenChannel) || visibleStreams[0] || null;

	const playGames = playableGames(overview.games, isLoggedIn ? activeLadderIds : []);
	const playGame = playGames.find((game) => game.ladder_id === playLadderId) || playGames[0];

	const selectTab = (tab) => {
		setSelectedTab(tab);
		if(playGames.some((game) => game.ladder_id === tab)) {
			setPlayLadderId(tab);
		}
	};

	// The picker saves each toggle as it happens and keeps myUser's preferred
	// games current, so the play bar reads them back once it closes. It loads on
	// demand: its models touch browser globals, which the server render lacks.
	const editGames = () => {
		import(/* webpackChunkName: "openPreferredGamesPicker" */ 'react/settings/openPreferredGamesPicker')
			.then(({ default: openPreferredGamesPicker }) => openPreferredGamesPicker().onDismiss)
			.then(() => setActiveLadderIds(myActiveLadderIds()));
	};

	return (
		<div className={'broadcast-hub' + (matchColumnShowing ? ' broadcast-hub--with-match' : '')}>
			{mounted && isLoggedIn && (
				<Suspense fallback={null}>
					<HomeMatchColumn />
				</Suspense>
			)}
			<div className='broadcast-hub__page'>
				<GameTabs
					games={overview.games}
					streams={overview.streams}
					selectedTab={selectedTab}
					onSelect={selectTab}
				/>
				<div className='broadcast-hub__stage'>
					<div className='broadcast-hub__main'>
						<FeaturedPlayer
							stream={featuredStream}
							game={featuredStream ? gamesById[featuredStream.ladder_id] : null}
							selectedGame={selectedTab === ALL_GAMES ? null : gamesById[selectedTab]}
							twitchAvailable={overview.twitch_available}
							embed={mounted}
						/>
						<PlayBar
							games={playGames}
							ladderId={playGame.ladder_id}
							onLadderChange={setPlayLadderId}
							onEditGames={isLoggedIn ? editGames : null}
							searchingCount={overview.searching_counts[playGame.ladder_id] || 0}
							isLoggedIn={isLoggedIn}
							currentMatch={playMatch}
							siteUrl={siteUrl}
							navigate={navigate}
						/>
					</div>
					<StreamRail
						streams={visibleStreams}
						gamesById={gamesById}
						featuredChannel={featuredStream ? featuredStream.channel : null}
						onWatch={setChosenChannel}
						news={news}
					/>
				</div>
				<div className='broadcast-hub__below'>
					<LiveMatchesPanel matches={visibleLiveMatches} gamesById={gamesById} siteUrl={siteUrl} />
					<SearchersPanel searchers={overview.searchers} gamesById={gamesById} siteUrl={siteUrl} />
					<RecentMatchesPanel
						matches={overview.recent_matches}
						gamesById={gamesById}
						siteUrl={siteUrl}
						now={renderedAt ? renderedAt * 1000 : Date.now()}
					/>
				</div>
				<HowItWorks />
			</div>
		</div>
	);
}

function defaultLadderId(games, preferredLadderIds) {
	const hubLadderIds = games.map((game) => game.ladder_id);
	const preferred = (preferredLadderIds || []).find((ladderId) => hubLadderIds.includes(ladderId));
	return preferred || DEFAULT_LADDER_ID;
}

// A member plays from their active games, in their own order; a guest, or a
// member with none of the hub's games active, from every hub game.
function playableGames(games, activeLadderIds) {
	const active = activeLadderIds
		.map((ladderId) => games.find((game) => game.ladder_id === ladderId))
		.filter(Boolean);
	return active.length > 0 ? active : games;
}

function myActiveLadderIds() {
	return Array.from(myUser.preferred_games.all().values())
		.filter((game) => game.preferred)
		.map((game) => Number(game.id));
}

// Games with the most live streams come first. The streams are the ones the page
// loaded with and never refresh, so the order holds while the page is open.
function GameTabs({ games, streams, selectedTab, onSelect }) {
	const liveCount = (ladderId) => streams.filter((stream) => stream.ladder_id === ladderId).length;
	const gameTabs = games
		.map((game) => ({ id: game.ladder_id, name: game.name, icon: game.icon_url, live: liveCount(game.ladder_id) }))
		.sort((first, second) => second.live - first.live);
	const tabs = [{ id: ALL_GAMES, name: __('All'), icon: null, live: streams.length }].concat(gameTabs);

	return (
		<div className='broadcast-hub__tabs' role='tablist' aria-label={__('Games')}>
			{tabs.map((tab) => (
				<button
					key={tab.id}
					type='button'
					role='tab'
					aria-selected={selectedTab === tab.id}
					className={'broadcast-hub__tab' + (selectedTab === tab.id ? ' is-active' : '')}
					onClick={() => onSelect(tab.id)}
				>
					{tab.icon && <img src={tab.icon} alt='' className='broadcast-hub__tab-icon' />}
					<span>{tab.name}</span>
					{tab.live > 0 && <span className='broadcast-hub__tab-live' aria-label={__('%s live', tab.live)}>{tab.live}</span>}
				</button>
			))}
		</div>
	);
}

function FeaturedPlayer({ stream, game, selectedGame, twitchAvailable, embed }) {
	if(!stream) {
		let message = __('Nobody is streaming right now.');
		if(!twitchAvailable) {
			message = __('Streams are unavailable right now.');
		} else if(selectedGame) {
			message = __('Nobody is streaming %s right now.', selectedGame.name);
		}
		return (
			<div className='broadcast-hub__player broadcast-hub__player--offline'>
				{selectedGame && <img src={selectedGame.logo_url} alt='' className='broadcast-hub__offline-logo' />}
				<p>{message}</p>
				<p className='broadcast-hub__offline-hint'>{__('You can still jump into a match below.')}</p>
			</div>
		);
	}

	return (
		<div className='broadcast-hub__player'>
			{embed
				? (
					<iframe
						key={stream.channel}
						src={twitchEmbedUrl(stream.channel)}
						title={__('%s live stream', stream.display_name)}
						allowFullScreen
					/>
				)
				: <img src={stream.thumbnail_url} alt='' className='broadcast-hub__player-poster' />}
			<div className='broadcast-hub__player-caption'>
				<a href={'https://www.twitch.tv/' + stream.channel} target='_blank' rel='noopener noreferrer' className='broadcast-hub__player-name'>
					{stream.display_name}
				</a>
				<span className='broadcast-hub__player-title'>{stream.title}</span>
				{game && <img src={game.small_logo_url} alt={game.name} className='broadcast-hub__player-game' />}
			</div>
		</div>
	);
}

function twitchEmbedUrl(channel) {
	return 'https://player.twitch.tv/?channel=' + encodeURIComponent(channel)
		+ '&parent=' + encodeURIComponent(window.location.hostname)
		+ '&muted=true&autoplay=true';
}
